'use client';

import * as z from 'zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { paths } from 'src/routes/paths';
import { useSearchParams } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';
import axiosInstance, { endpoints } from 'src/lib/axios';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { getErrorMessage } from '../../utils';
import { FormHead } from '../../components/form-head';

// ----------------------------------------------------------------------

export function JwtResetPasswordView() {
  const { t } = useTranslate();

  const searchParams = useSearchParams();
  const token = searchParams.get('token');

  const showPassword = useBoolean();
  const showConfirm = useBoolean();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const ResetPasswordSchema = useMemo(
    () =>
      z
        .object({
          password: z
            .string()
            .min(8, { error: t('auth.validation.passwordMin8') })
            .max(72, { error: t('auth.validation.passwordMax72') }),
          confirmPassword: z.string(),
        })
        .refine((data) => data.password === data.confirmPassword, {
          error: t('auth.validation.passwordsMustMatch'),
          path: ['confirmPassword'],
        }),
    [t]
  );

  const methods = useForm({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    if (!token) {
      setErrorMessage(t('auth.resetPasswordInvalidToken'));
      return;
    }
    try {
      await axiosInstance.post(endpoints.auth.confirmReset, {
        token,
        new_password: data.password,
      });
      setSuccessMessage(t('auth.resetPasswordSuccess'));
      setErrorMessage(null);
    } catch (error) {
      console.error(error);
      setErrorMessage(getErrorMessage(error));
    }
  });

  if (!token) {
    return (
      <>
        <FormHead
          title={t('auth.resetPasswordTitle')}
          sx={{ textAlign: { xs: 'center', md: 'left' } }}
        />
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('auth.resetPasswordInvalidToken')}
        </Alert>
        <Link
          component={RouterLink}
          href={paths.auth.jwt.forgotPassword}
          variant="body2"
          color="inherit"
          sx={{ display: 'block', textAlign: 'center' }}
        >
          {t('auth.requestNewResetLink')}
        </Link>
      </>
    );
  }

  return (
    <>
      <FormHead
        title={t('auth.resetPasswordTitle')}
        description={t('auth.resetPasswordSubtitle')}
        sx={{ textAlign: { xs: 'center', md: 'left' } }}
      />

      {!!successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {!!errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {!successMessage ? (
        <Form methods={methods} onSubmit={onSubmit}>
          <Box sx={{ gap: 3, display: 'flex', flexDirection: 'column' }}>
            <Field.Text
              name="password"
              label={t('auth.newPassword')}
              placeholder={t('auth.passwordHint8')}
              type={showPassword.value ? 'text' : 'password'}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={showPassword.onToggle} edge="end">
                        <Iconify
                          icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Field.Text
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              type={showConfirm.value ? 'text' : 'password'}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={showConfirm.onToggle} edge="end">
                        <Iconify
                          icon={showConfirm.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              fullWidth
              color="inherit"
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {t('auth.resetPasswordButton')}
            </Button>
          </Box>
        </Form>
      ) : (
        <Link
          component={RouterLink}
          href={paths.auth.jwt.signIn}
          variant="body2"
          color="inherit"
          sx={{ display: 'block', textAlign: 'center' }}
        >
          {t('auth.backToSignIn')}
        </Link>
      )}
    </>
  );
}
